import React from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { connect, ConnectedProps } from "react-redux";
import { Field, formValueSelector, InjectedFormProps, reduxForm } from "redux-form";
import {
  Button,
  Dimmer,
  Form,
  Grid,
  Header,
  Image,
  Loader,
  Message,
  Segment
} from "semantic-ui-react";
import { startResetPassword } from "../../actions/users/users";
import logo from "../../assets/images/urbansim/logo.png";
import { reCaptchaSiteKey } from "../../config/config";
import { AppDispatch, RootState } from "../../reducers";
import { disable2faByRecoveryCode } from "../../store/twoFactorAuthentication";
import CustomLoader from "../common/CustomLoader";
import TextInput from "../forms/TextInput";
import PasswordResetFormModal from "../users/PasswordResetFormModal";
import RecoveryCodesResetFormModal from "./RecoveryCodesResetFormModal";

const reduxFormName = "login";

const selector = formValueSelector(reduxFormName);

const mapStateToProps = (state: RootState) => {
  return {
    loginFail: state.users.loginFail,
    loggingIn: state.users.loggingIn,
    loginFailMessage: state.users.loginFailMessage,
    isAuthenticating: state.users.isAuthenticating,
    openResetPasswordModal: state.users.openResetPasswordModal,
    submittingResetPassword: state.users.submittingResetPassword,
    resetPasswordFailed: state.users.resetPasswordFailed,
    resetPasswordMessage: state.users.resetPasswordMessage,
    username: selector(state, "username"),
    password: selector(state, "password"),
    display2FALoginField: state.users.display2FALoginField,
    disable2faByRecoveryCodeFailure: state.twoFactorAuthentication.disable2faByRecoveryCodeFailure,
    disable2faByRecoveryCodeMessage: state.twoFactorAuthentication.disable2faByRecoveryCodeMessage,
    disable2faByRecoveryCodeSuccess: state.twoFactorAuthentication.disable2faByRecoveryCodeSuccess,
    isDisable2faByRecoveryCodeLoading:
      state.twoFactorAuthentication.isDisable2faByRecoveryCodeLoading
  };
};

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  startResetPassword: (email: string) => dispatch(startResetPassword(email)),
  disable2faByRecoveryCode: (recoveryCode: string, username: string, password: string) =>
    dispatch(disable2faByRecoveryCode(recoveryCode, username, password))
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

type LoginProps = {
  /** On form submit function. */
  onSubmit: (values: any) => void;
  /** Should display created account success message?. */
  showCreatedAccountSuccessMessage: boolean;
} & PropsFromRedux;

type LoginState = { isCaptchaSolved: boolean };

type LoginFormProps = LoginProps & InjectedFormProps<{}, LoginProps>;

class Login extends React.Component<LoginFormProps, LoginState> {
  private reCaptchaRef: React.RefObject<any>;

  constructor(props: LoginFormProps) {
    super(props);
    this.state = {
      isCaptchaSolved: false
    };

    this.reCaptchaRef = React.createRef();
  }

  componentDidUpdate(prevProps: LoginFormProps): void {
    if (
      (this.props.loginFail && !prevProps.loginFail) ||
      (this.props.display2FALoginField && !prevProps.display2FALoginField)
    ) {
      this.reCaptchaRef.current.reset();
      this.setState({ isCaptchaSolved: false });
    }
  }

  isLoginEnabled = () => {
    return this.state.isCaptchaSolved && this.props.username && this.props.password;
  };

  handleRecaptchaFailure = (field: any) => {
    this.setState({ isCaptchaSolved: false });
    field.input.onChange();
  };

  handleRecaptchaSuccess = (field: any, value: string | null) => {
    this.setState({ isCaptchaSolved: true });
    field.input.onChange(value);
  };

  handleRecoveryCodeSubmit = (values: any) => {
    this.props.disable2faByRecoveryCode(
      values.recoveryCode,
      this.props.username,
      this.props.password
    );
  };

  renderRecaptchaField = (field: any) => {
    return (
      <ReCAPTCHA
        ref={this.reCaptchaRef}
        // @ts-ignore
        style={{ marginBottom: "20px" }}
        name="recaptcha"
        align="center"
        sitekey={reCaptchaSiteKey}
        onChange={value => this.handleRecaptchaSuccess(field, value)}
        onErrored={() => this.handleRecaptchaFailure(field)}
        onExpired={() => this.handleRecaptchaFailure(field)}
      />
    );
  };

  /**
   * Render user-facing messages
   */
  renderMessages = () => {
    if (this.props.loginFail) {
      return <Message negative>{this.props.loginFailMessage}</Message>;
    }
    if (this.props.disable2faByRecoveryCodeFailure) {
      return <Message negative>{this.props.disable2faByRecoveryCodeMessage}</Message>;
    }
    if (this.props.disable2faByRecoveryCodeSuccess) {
      return <Message positive>Two-factor authentication has been successfully disabled</Message>;
    }
    return null;
  };

  render() {
    if (this.props.isAuthenticating) return <CustomLoader />;
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          height: "100vh"
        }}
      >
        <Dimmer active={this.props.loggingIn}>
          <Loader />
        </Dimmer>
        <Grid textAlign="center" verticalAlign="middle">
          <Grid.Column style={{ maxWidth: 450 }}>
            <Header as="h2" textAlign="center" style={{ marginTop: "-60px" }}>
              <Image src={logo} aria-label="Urbancanvas logo" /> UrbanSim Account Manager
            </Header>
            <Form size="large" onSubmit={this.props.handleSubmit}>
              <Segment>
                <Field
                  name="username"
                  component={TextInput}
                  fluid
                  icon="user"
                  iconPosition="left"
                  placeholder="Enter your username or e-mail address"
                  autoComplete="off"
                  required
                />
                <Field
                  name="password"
                  component={TextInput}
                  fluid
                  icon="lock"
                  iconPosition="left"
                  placeholder="Password"
                  type="password"
                  autoComplete="off"
                  required
                />
                {this.props.display2FALoginField ? (
                  <>
                    <Field
                      name="code"
                      maxLength="6"
                      component={TextInput}
                      fluid
                      icon="mobile"
                      iconPosition="left"
                      placeholder="6-digit device verification code"
                      autoComplete="off"
                      required
                    />
                    <RecoveryCodesResetFormModal onSubmit={this.handleRecoveryCodeSubmit} />
                  </>
                ) : null}
                <Field name="captchaToken" component={this.renderRecaptchaField} required />
                <PasswordResetFormModal
                  onSubmit={(values: any) => this.props.startResetPassword(values)}
                />
                <Button type="submit" color="green" fluid size="large">
                  Login
                </Button>
              </Segment>
            </Form>
            {this.renderMessages()}
          </Grid.Column>
        </Grid>
      </div>
    );
  }
}

// Disabled for now
//<Segment>
//  Do not have an account yet? <Link to={amRoutes.accountCreate}>Create an account.</Link>
//</Segment>;

const LoginForm = reduxForm<{}, LoginProps>({
  form: reduxFormName
})(Login);

export default connect(mapStateToProps, mapDispatchToProps)(LoginForm);
